import React, { Component } from 'react';
import { array, bool, func, oneOf, object, shape, string, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { createResourceLocatorString } from '../../util/routes';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  getQueryParamNames,
  isOriginInUse,
} from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';
import { customModifySearchFilters, cleanFiltersFromUnwantedOptions } from '../../util/misc';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { managerForCategory } from '../../ducks/categoryAccountManagers.duck';

import {
  H3,
  H5,
  Page,
  AccountManagerCard,
  ACCOUNT_MANAGER_CARD_SEARCH,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
} from './SearchPage.shared';

import { getLocationFilters } from './locationFilters';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import css from './SearchPage.module.css';
import DealsTabs from '../../components/MemberArea/DealsTabs/DealsTabs';
import { memberAreaEnabled } from '../../util/memberArea';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      isFiltersOpenOnMobile: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);
    this.onToggleMobileFilter = this.onToggleMobileFilter.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);

    // Mobile filters height, for the account manager strip's sticky offset
    this.searchResultContainerRef = React.createRef();
    this.mobileFiltersRef = React.createRef();
    this.mobileFiltersObserver = null;
  }

  // On mobile the account manager strip sticks just under the sticky filters
  // box. That box's height depends on its text, so it is measured here and
  // written as --mobileFiltersHeight for the strip's `top` to read. A CSS
  // variable, not state, so a resize does not re-render the whole page. See
  // .accountManagerStrip in SearchPage.module.css.
  componentDidMount() {
    const filters = this.mobileFiltersRef.current;
    if (!filters || typeof window === 'undefined' || !window.ResizeObserver) {
      return;
    }

    this.mobileFiltersObserver = new window.ResizeObserver(() => {
      const container = this.searchResultContainerRef.current;
      if (container) {
        container.style.setProperty('--mobileFiltersHeight', `${filters.offsetHeight}px`);
      }
    });
    this.mobileFiltersObserver.observe(filters);
  }

  componentWillUnmount() {
    if (this.mobileFiltersObserver) {
      this.mobileFiltersObserver.disconnect();
    }
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  onToggleMobileFilter(boolean) {
    this.setState({ isFiltersOpenOnMobile: boolean });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);
    const allQueryParamNames = [
      ...filterQueryParamNames,
      "pub_listingType",
      "pub_subcategory",
      "pub_region",
      "pub_country",
    ];

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params
    const queryParams = omit(urlQueryParams, allQueryParamNames);
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  getHandleChangedValueFn(useHistoryPush, componentId) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        // const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        // nl: uncomment above and remove below to reset keywords filter state
        const keywordsMaybe = { keywords: undefined };

        const omitedReadyQueryParams = omitLimitedListingFieldParams(
          {
            ...mergedQueryParams,
            ...updatedURLParams,
            ...keywordsMaybe,
            address,
            bounds,
          },
          filterConfigs
        );

        const customCleanedFilters = customModifySearchFilters(omitedReadyQueryParams);

        return {
          currentQueryParams: customCleanedFilters,
        };
      };
      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);
          const hashMaybe = componentId
            ? `#${componentId}.plain.form`
            : '';

          history.push(createResourceLocatorString(
            'SearchPage',
            routeConfiguration,
            {},
            search,
            hashMaybe,
          ));
        }
      };
      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      routeConfiguration,
      config,
      currentUser,
      managersByCategory,
    } = this.props;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );
    const validQueryParams = urlQueryParams;
    const searchParsed = parse(location.search);

    // The account manager card above the results.
    //
    // It appears only when the category filter has resolved to exactly ONE
    // vertical. `validQueryParams` has already been through
    // validURLParamForCategoryData, which keeps pub_categoryLevel1 only when it
    // matches one category id exactly — so a truthy value here already means
    // one, and anything else (no filter, or a value that matched nothing) leaves
    // selectedCategory null and the card out. This is the same lookup
    // MainPanelHeader uses for its heading.
    //
    // Nothing here watches the filter: the whole render re-runs when the URL
    // changes, and the manager comes from a map that is already in the store, so
    // the card swaps with no request and no page load.
    const selectedCategory =
      Array.isArray(listingCategories) && listingCategories.length > 0
        ? listingCategories.find(c => c?.id === validQueryParams?.pub_categoryLevel1)
        : null;

    const categoryManager = managerForCategory(managersByCategory, selectedCategory?.id);

    // The category heading (phones only) and the card sit in a full-width sticky
    // strip, so the results scroll fully behind it. The strip's offset depends
    // on the topbar height and so on whether the reader is signed in — the same
    // split as mainPanelClass below. A category with no manager still gets the
    // strip on a phone, for its heading; from --viewportMedium up it would be
    // empty, so .categoryStripNoManager hides it there.
    const categoryStrip = selectedCategory ? (
      <div
        className={classNames(
          !!currentUser ? css.accountManagerStrip : css.accountManagerStripNotLogged,
          { [css.categoryStripNoManager]: !categoryManager }
        )}
      >
        {/* Phones only. From --viewportMedium up MainPanelHeader is the
            heading, but it is hidden below that. */}
        <h2 className={css.categoryHeadingMobile}>{selectedCategory.name}</h2>
        {categoryManager ? (
          <AccountManagerCard
            manager={categoryManager}
            variant={ACCOUNT_MANAGER_CARD_SEARCH}
            mailtoSubject={intl.formatMessage(
              { id: 'AccountManagerCard.searchSubject' },
              { category: selectedCategory.name }
            )}
            whatsappText={intl.formatMessage(
              { id: 'AccountManagerCard.searchWhatsappText' },
              { firstName: categoryManager.firstName, category: selectedCategory.name }
            )}
            onManageDisableScrolling={onManageDisableScrolling}
          />
        ) : null}
      </div>
    ) : null;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => !['keywords', 'categoryLevel'].includes(f.key))
      : defaultFiltersConfig.filter(f => !['categoryLevel'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );

    const listingTypeFilter = {
      key: "listingType",
      schemaType: "listingType",
      scope: "public",
      isNestedEnum: false,
      options: config.listing.listingTypes,
    };

    const dealSizeFilterMaybe = customPrimaryFilters.filter(f => f.key == "dealSizeBracketFilter");
    const restCustomPrimaryFilters = customPrimaryFilters.filter(f => f.key !== "dealSizeBracketFilter");

    const cleanedPrimaryFilters = cleanFiltersFromUnwantedOptions(restCustomPrimaryFilters);

    const categorySpecificFilters = cleanedPrimaryFilters.filter(f => f.categoryConfig?.limitToCategoryIds);
    const nonCategorySpecificFilters = cleanedPrimaryFilters.filter(f => !f.categoryConfig?.limitToCategoryIds);

    const locationFilters = getLocationFilters(searchParsed);

    const availableFilters = [
      listingTypeFilter,
      ...builtInPrimaryFilters,
      ...nonCategorySpecificFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
      ...locationFilters,
      ...dealSizeFilterMaybe,
    ];

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
          ? listings.length
          : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );

    const layoutContainerClass = !!currentUser
      ? css.layoutWrapperContainer
      : css.layoutWrapperContainerNotLogged;
    // Member area, signed in: the page sits beside the sidebar (see the CSS).
    const memberLayout = memberAreaEnabled() && !!currentUser;
    const layoutFilterClass = !!currentUser
      ? css.filterColumnContent
      : css.filterColumnContentNotLogged;
    const mainPanelClass = !!currentUser
      ? css.mainPanel
      : css.mainPanelNotLogged;
    
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
      />
    );

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    // Member area: the pinned top bar starts after the sidebar, so it doesn't
    // cover the logo and "New listing".
    const topbarClasses = classNames(css.topbar, {
      [css.topbarBehindModal]: this.state.isMobileModalOpen,
      [css.topbarMember]: memberAreaEnabled(),
    });

    const CategoryNestedFiltersComponent = params => {
      const { isMobile } = params;
      return <div className={css.categoryNestedFiltersWrapper}>
        {categorySpecificFilters.map(filterConfig => {
          const key = `SearchFilters${isMobile ? "Mobile" : "Desktop"}.${filterConfig.scope || 'built-in'}.${filterConfig.key
            }`;

          return (
            <FilterComponent
              key={key}
              idPrefix={isMobile ? "SearchFiltersMobile" : "SearchFiltersDesktop"}
              className={isMobile ? null : css.filter}
              config={filterConfig}
              listingCategories={listingCategories}
              marketplaceCurrency={marketplaceCurrency}
              urlQueryParams={validQueryParams}
              onCloseMobileModal={this.onToggleMobileFilter}
              initialValues={initialValues(this.props, this.state.currentQueryParams)}
              getHandleChangedValueFn={this.getHandleChangedValueFn}
              intl={intl}
              liveEdit
              showAsPopup={false}
              search={searchParsed}
              isDesktop={!isMobile}
              isNestedFieldInCategory={true}
            />
          );
        })}
      </div>
    };

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <div className={classNames(layoutContainerClass, { [css.layoutMember]: memberLayout })}>
          <aside
            className={classNames(css.layoutWrapperFilterColumn, {
              [css.filterColumnMember]: memberLayout,
            })}
            data-testid="filterColumnAside"
          >
            <div className={layoutFilterClass}>
              {availableFilters.map(filterConfig => {
                const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${filterConfig.key
                  }`;
                return (
                  <FilterComponent
                    key={key}
                    idPrefix="SearchFiltersDesktop"
                    className={css.filter}
                    config={filterConfig}
                    listingCategories={listingCategories}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    initialValues={initialValues(this.props, this.state.currentQueryParams)}
                    getHandleChangedValueFn={this.getHandleChangedValueFn}
                    intl={intl}
                    liveEdit
                    showAsPopup={false}
                    isDesktop
                    search={searchParsed}
                    categoryNestedFilters={<CategoryNestedFiltersComponent isMobile={false} />}
                  />
                );
              })}
              <button className={css.resetAllButton} onClick={e => this.handleResetAll(e)}>
                <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
              </button>
            </div>
          </aside>

          <div
            className={classNames(css.layoutWrapperMain, { [css.layoutWrapperMainMember]: memberLayout })}
            role="main"
          >
            <div className={css.searchResultContainer} ref={this.searchResultContainerRef}>
              {/* Member area: "Deals" with its tabs (Member Home design). */}
              {memberAreaEnabled() ? (
                <DealsTabs active="explore" exploreCount={totalItems} />
              ) : null}
              <SearchFiltersMobile
                rootRef={this.mobileFiltersRef}
                className={css.searchFiltersMobileList}
                config={config}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                isFiltersOpenOnMobile={this.state.isFiltersOpenOnMobile}
                onToggleMobileFilter={this.onToggleMobileFilter}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                isMapVariant={false}
                noResultsInfo={noResultsInfo}
                currentUser={currentUser}
              >
                {availableFilters.map(filterConfig => {
                  const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${filterConfig.key
                    }`;

                  return (
                    <FilterComponent
                      key={key}
                      idPrefix="SearchFiltersMobile"
                      config={filterConfig}
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      onCloseMobileModal={this.onToggleMobileFilter}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                      search={searchParsed}
                      categoryNestedFilters={<CategoryNestedFiltersComponent isMobile={true} />}
                    />
                  );
                })}
              </SearchFiltersMobile>
              <MainPanelHeader
                className={mainPanelClass}
                sortByComponent={sortBy('desktop')}
                isSortByActive={sortConfig.active}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                config={config}
                urlQueryParams={urlQueryParams}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                noResultsInfo={noResultsInfo}
                currentUser={currentUser}
              />
              <div
                className={classNames(css.listingsForGridVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>
                    <FormattedMessage id="SearchPage.invalidDatesFilter" />
                  </H5>
                ) : null}
                {/* Above the first listing card, inside the scrolling results
                    column so its strip can stick to the top of it. */}
                {categoryStrip}
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={listings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={searchParsed}
                  searchString={location.search}
                  isMapVariant={false}
                  currentUser={currentUser}
                />
              </div>
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

SearchPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  managersByCategory: {},
};

SearchPageComponent.propTypes = {
  listings: array,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,
  /** Account managers keyed by listing category id. An empty map is normal. */
  managersByCategory: object,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);
  const { managersByCategory } = state.categoryAccountManagers;

  return {
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    currentUser,
    managersByCategory,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedSearchPage);

export default SearchPage;
